import React, { useEffect, useState } from 'react';
import { BedtimePair } from '../../context/bedtime/bedtimeContextModel';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { CloseIcon } from '../icons';
import { PillButton, Sheet } from '../ui';
import './BedtimePairSheet.css';

export const BEDTIME_PAIR_SHEET_ID = 'bedtime-pair-sheet';

export function isUsableVoice(voice: { status: string; consent_active?: boolean | null }): boolean {
  return voice.status === 'ready' && voice.consent_active === true;
}

interface BedtimePairSheetProps {
  /** Open the sheet once when the family needs a first selection. Off on management routes. */
  autoOpen?: boolean;
}

/**
 * The one place the listener and voice are chosen. Mounted once in the parent
 * shell so every `openPairDialog()` caller (chip, empty states, Tonight,
 * setup, gifts, voice details) opens the same dialog. Selection logic, IDs and
 * persistence are the former BedtimeContextBar's, unchanged.
 */
const BedtimePairSheet: React.FC<BedtimePairSheetProps> = ({ autoOpen = false }) => {
  const context = useBedtimeContext();
  const [dialogHouseholdId, setDialogHouseholdId] = useState('');
  const [dialogListenerId, setDialogListenerId] = useState('');
  const [dialogVoiceId, setDialogVoiceId] = useState('');
  const [autoOpened, setAutoOpened] = useState(false);

  const seededVoice = context.voices.find((voice) => voice.voice_id === context.pairDialogSeed?.voiceId);
  const selectedHousehold = context.households.find((household) => household.household_id === dialogHouseholdId);
  const dialogListeners = context.listeners.filter((listener) => listener.household_id === dialogHouseholdId);
  const dialogVoices = context.voices.filter((voice) => (
    voice.household_id === dialogHouseholdId
    || (context.households.length === 1 && voice.household_id == null)
  ));
  const draftListener = context.listeners.find((listener) => listener.listener_id === dialogListenerId);
  const draftVoice = dialogVoices.find((voice) => voice.voice_id === dialogVoiceId);
  const draftBookends = draftVoice && draftListener
    ? context.getPairBookends(draftVoice.voice_id, draftListener.listener_id)
    : undefined;
  const draftNeedsBookends = Boolean(
    draftVoice && draftListener && isUsableVoice(draftVoice)
      && (!draftBookends?.intro || !draftBookends?.goodnight)
  );

  useEffect(() => {
    if (autoOpen && context.status === 'needs_selection' && !autoOpened) {
      setAutoOpened(true);
      context.openPairDialog();
    }
  }, [autoOpen, autoOpened, context]);

  useEffect(() => {
    if (!context.pairDialogOpen) return;
    const seededHouseholdId = context.pairDialogSeed?.householdId
      || seededVoice?.household_id
      || context.pair?.householdId
      || (context.households.length === 1 ? context.households[0].household_id : '');
    const currentListeners = context.listeners.filter((listener) => listener.household_id === seededHouseholdId);
    const voices = context.voices.filter((voice) => (
      voice.household_id === seededHouseholdId
      || (context.households.length === 1 && voice.household_id == null)
    ));
    const usableVoices = voices.filter(isUsableVoice);
    setDialogHouseholdId(seededHouseholdId);
    setDialogListenerId(
      context.pairDialogSeed?.listenerId
      || context.pair?.listenerId
      || (currentListeners.length === 1 ? currentListeners[0].listener_id : '')
    );
    setDialogVoiceId(
      context.pairDialogSeed?.voiceId
      || context.pair?.voiceId
      || (usableVoices.length === 1 ? usableVoices[0].voice_id : '')
    );
  }, [context.pair, context.pairDialogOpen, context.pairDialogSeed, context.households, context.listeners, context.voices, seededVoice]);

  const voicesFor = (householdId: string) => context.voices.filter((voice) => (
    voice.household_id === householdId
    || (context.households.length === 1 && voice.household_id == null)
  ));

  const onHouseholdChange = (householdId: string) => {
    setDialogHouseholdId(householdId);
    const listeners = context.listeners.filter((listener) => listener.household_id === householdId);
    const usableVoices = voicesFor(householdId).filter(isUsableVoice);
    setDialogListenerId(listeners.length === 1 ? listeners[0].listener_id : '');
    setDialogVoiceId(usableVoices.length === 1 ? usableVoices[0].voice_id : '');
  };

  const savePair = () => {
    if (!dialogHouseholdId || !dialogListenerId || !dialogVoiceId || !draftVoice || !isUsableVoice(draftVoice)) return;
    const pair: BedtimePair = { householdId: dialogHouseholdId, listenerId: dialogListenerId, voiceId: dialogVoiceId };
    context.setPair(pair);
  };

  const canSave = Boolean(dialogHouseholdId && dialogListenerId && dialogVoiceId && draftVoice && isUsableVoice(draftVoice));

  return (
    <>
      {context.notice && (
        <div className="bedtime-notice" role="status" aria-live="polite">
          <span>{context.notice}</span>
          <button type="button" className="bedtime-notice__dismiss" onClick={context.clearNotice} aria-label="Dismiss notice">
            <CloseIcon size={20} />
          </button>
        </div>
      )}
      <Sheet
        id={BEDTIME_PAIR_SHEET_ID}
        open={context.pairDialogOpen}
        title="Who is tonight’s story for?"
        closeLabel="Close bedtime choices"
        describedBy={`${BEDTIME_PAIR_SHEET_ID}-intro`}
        onClose={context.closePairDialog}
      >
        <p id={`${BEDTIME_PAIR_SHEET_ID}-intro`} className="bedtime-sheet__intro">Your choice stays with you as you browse and listen.</p>

        <fieldset className="bedtime-options" disabled={!context.households.length}>
          <legend>Family</legend>
          {context.households.map((item) => (
            <label className="bedtime-option" key={item.household_id}>
              <input type="radio" name="bedtime-household" value={item.household_id} checked={dialogHouseholdId === item.household_id} onChange={() => onHouseholdChange(item.household_id)} />
              <span className="bedtime-option__main">{item.current_user_role === 'owner' ? 'Your family' : 'Shared family'}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="bedtime-options" disabled={!selectedHousehold}>
          <legend>Listener</legend>
          {dialogListeners.length ? dialogListeners.map((item) => (
            <label className="bedtime-option" key={item.listener_id}>
              <input type="radio" name="bedtime-listener" value={item.listener_id} checked={dialogListenerId === item.listener_id} onChange={() => setDialogListenerId(item.listener_id)} />
              <span className="bedtime-option__main">{item.display_name}</span>
            </label>
          )) : <p className="bedtime-options__empty">Choose a family with an active listener.</p>}
        </fieldset>

        <fieldset className="bedtime-options" disabled={!selectedHousehold}>
          <legend>Family voice</legend>
          {dialogVoices.length ? dialogVoices.map((item) => {
            const usable = isUsableVoice(item);
            const label = item.status === 'ready' && item.consent_active !== true
              ? 'Needs family consent'
              : item.status === 'ready'
                ? 'Ready'
                : item.status === 'training' || item.status === 'processing'
                  ? 'Still preparing'
                  : 'Needs attention';
            const bookends = dialogListenerId ? context.getPairBookends(item.voice_id, dialogListenerId) : undefined;
            const needsBookends = usable && dialogListenerId && (!bookends?.intro || !bookends?.goodnight);
            return (
              <label className="bedtime-option" key={item.voice_id}>
                <input type="radio" name="bedtime-voice" value={item.voice_id} checked={dialogVoiceId === item.voice_id} disabled={!usable} onChange={() => setDialogVoiceId(item.voice_id)} />
                <span className="bedtime-option__main">
                  <span>{item.voice_name}</span>
                  <small>{needsBookends ? `Needs ${draftListener?.display_name || 'this listener'}’s hello and goodnight.` : label}</small>
                </span>
              </label>
            );
          }) : <p className="bedtime-options__empty">Add a family voice to get started.</p>}
        </fieldset>

        {draftNeedsBookends && draftListener && (
          <p className="bedtime-sheet__warning" role="status">Needs {draftListener.display_name}’s hello and goodnight before a new story can be recorded or started.</p>
        )}
        <div className="bedtime-sheet__actions">
          <PillButton variant="secondary" onClick={context.closePairDialog}>Not now</PillButton>
          <PillButton variant="primary" disabled={!canSave} onClick={savePair}>Use for bedtime</PillButton>
        </div>
      </Sheet>
    </>
  );
};

export default BedtimePairSheet;
